/**
 * 롤별메뉴접근관리 트리 유틸 (순수 함수)
 *
 * - authMap: { [`${sytmidno}_${menuflag}`]: { view, upyn, dlyn } } — 조회(view) 'Y'인 노드(= EMSINFO_AUTH 행이 있는 메뉴)만 보관
 * - 칸: 조회(행이 있음) · 등록·수정(UPYN, 저장할 때 REYN 도 같은 값) · 삭제(DLYN)
 * - 트리: 시스템(EMSINFO_SYTM) > 상위 메뉴(MENUCODE=0) > 하위 메뉴(MENUCODE=상위 MENUFLAG)
 * - 사용 안 하는 메뉴(MENUUSE 'N', 상위 메뉴가 미사용이면 하위도, 하위 메뉴가 모두 미사용이면 상위도)는 node.unused.
 *   개수는 사용 메뉴만 세고 미사용은 따로 센다
 */

export const EMPTY_AUTH = Object.freeze({ view: 'N', upyn: 'N', dlyn: 'N' });

export const authKey = (sytmidno, menuflag) => `${sytmidno}_${menuflag}`;

const yn = (value) => (String(value ?? '').toUpperCase() === 'Y' ? 'Y' : 'N');

const bySeq = (a, b) => (Number(a.menusequ ?? 0) - Number(b.menusequ ?? 0)) || (Number(a.menuflag) - Number(b.menuflag));

/**
 * selectRoleMenuTree.do 평면 행 → 시스템/상위/하위 트리 + 현재 권한 맵
 * @param {Array} rows [{SYTMIDNO, SYTMNAME, SYTMSEQU, MENUFLAG, MENUCODE, MENUSEQU, MENUNAME, MENUSORC, MENUUSE, GRANTYN, REYN, UPYN, DLYN, MODID, MODDT}]
 * @returns {{systems: Array, authMap: Object}}
 */
export const buildMenuTree = (rows = []) => {
    const systemMap = new Map();
    const authMap = {};

    // 1차: 노드 생성
    rows.forEach((row) => {
        const sytmidno = Number(row.SYTMIDNO);
        if (!systemMap.has(sytmidno)) {
            systemMap.set(sytmidno, {
                sytmidno,
                sytmname: row.SYTMNAME,
                sytmsequ: Number(row.SYTMSEQU ?? 0),
                menus: [],
                byFlag: new Map(),
            });
        }
        const system = systemMap.get(sytmidno);
        const menuflag = Number(row.MENUFLAG);
        if (system.byFlag.has(menuflag)) return; // 동일 (시스템, 메뉴) 중복 행 방어

        const node = {
            key: authKey(sytmidno, menuflag),
            sytmidno,
            menuflag,
            menucode: Number(row.MENUCODE ?? 0),
            menusequ: Number(row.MENUSEQU ?? 0),
            menuname: row.MENUNAME,
            menusorc: row.MENUSORC ?? null,
            unused: String(row.MENUUSE ?? 'Y').toUpperCase() === 'N',
            modid: row.MODID ?? '',
            moddt: row.MODDT ?? '',
            children: [],
        };
        system.byFlag.set(menuflag, node);

        // 행이 있으면 조회 권한이다(REYN 값과 상관없이). 등록·수정은 UPYN 으로 읽는다
        if (yn(row.GRANTYN) === 'Y') {
            authMap[node.key] = { view: 'Y', upyn: yn(row.UPYN), dlyn: yn(row.DLYN) };
        }
    });

    // 2차: 부모-자식 연결 (부모가 같은 시스템에 없으면 상위로 승격)
    systemMap.forEach((system) => {
        system.byFlag.forEach((node) => {
            if (node.menucode === 0) {
                system.menus.push(node);
                return;
            }
            const parent = system.byFlag.get(node.menucode);
            if (parent && parent !== node) {
                parent.children.push(node);
                if (parent.unused) node.unused = true;
            } else {
                node.orphan = true;
                system.menus.push(node);
            }
        });
        // 화면이 없는 상위 메뉴인데 하위 메뉴가 모두 미사용이면 사이드바에 안 나오므로 상위 메뉴도 미사용으로 본다
        system.menus.forEach((menu) => {
            if (!menu.unused && !menu.menusorc && menu.children.length > 0 && menu.children.every((child) => child.unused)) {
                menu.unused = true;
                menu.unusedByChildren = true;
            }
        });
    });

    const systems = [...systemMap.values()]
        .map((system) => {
            system.menus.sort(bySeq);
            system.menus.forEach((menu) => menu.children.sort(bySeq));
            const { byFlag, ...rest } = system;
            return rest;
        })
        .sort((a, b) => (a.sytmsequ - b.sytmsequ) || (a.sytmidno - b.sytmidno));

    return { systems, authMap };
};

export const getAuth = (authMap, key) => authMap[key] || EMPTY_AUTH;

/**
 * 노드 1개의 플래그 변경
 * - 조회(view) 해제 → 등록·수정 · 삭제도 해제(노드 제거)
 * - 등록·수정 / 삭제 체크 → 조회 자동 체크
 */
export const applyFlag = (authMap, key, field, checked) => {
    const current = { ...getAuth(authMap, key) };

    if (field === 'view') {
        if (checked) {
            current.view = 'Y';
        } else {
            current.view = 'N';
            current.upyn = 'N';
            current.dlyn = 'N';
        }
    } else {
        current[field] = checked ? 'Y' : 'N';
        if (checked) current.view = 'Y';
    }

    const next = { ...authMap };
    if (current.view === 'N') {
        delete next[key];
    } else {
        next[key] = current;
    }
    return next;
};

/** 노드 + 하위 메뉴 전체에 플래그 변경 적용. includeUnused 가 false 면 미사용 하위 메뉴(화면에서 숨긴 메뉴)는 그대로 둔다 */
export const applyFlagCascade = (authMap, node, field, checked, includeUnused = true) => {
    let next = applyFlag(authMap, node.key, field, checked);
    (node.children || []).forEach((child) => {
        if (!includeUnused && child.unused) return;
        next = applyFlagCascade(next, child, field, checked, includeUnused);
    });
    return next;
};

/**
 * 시스템 내 메뉴 조회 권한 일괄 설정/해제. 설정은 사용 메뉴에만,
 * 해제는 includeUnused 면 미사용 메뉴까지, 아니면(미사용 메뉴를 숨겼을 때) 사용 메뉴만
 */
export const applySystemAll = (authMap, system, checked, includeUnused = true) => {
    let next = authMap;
    const walk = (node) => {
        if (node.unused && (checked || !includeUnused)) return;
        next = applyFlag(next, node.key, 'view', checked);
        (node.children || []).forEach(walk);
    };
    (system.menus || []).forEach(walk);
    return next;
};

/** 트리의 노드 키 (시스템 또는 노드 배열). includeUnused 가 false 면 사용 메뉴만 */
export const collectKeys = (nodes = [], includeUnused = true) => {
    const keys = [];
    const walk = (node) => {
        if (includeUnused || !node.unused) keys.push(node.key);
        (node.children || []).forEach(walk);
    };
    nodes.forEach(walk);
    return keys;
};

/** 노드 집합의 부여 상태: 'all' | 'some' | 'none'. includeUnused 가 false 면 사용 메뉴만 본다 */
export const grantState = (authMap, nodes = [], includeUnused = true) => {
    const keys = collectKeys(nodes, includeUnused);
    if (keys.length === 0) return 'none';
    const granted = keys.filter((key) => getAuth(authMap, key).view === 'Y').length;
    if (granted === 0) return 'none';
    return granted === keys.length ? 'all' : 'some';
};

const countKeys = (authMap, keys) => keys.filter((key) => getAuth(authMap, key).view === 'Y').length;

/** 시스템별 (사용 메뉴 부여 수, 사용 메뉴 수, 미사용 메뉴 부여 수, 미사용 메뉴 수) */
export const countSystem = (authMap, system) => {
    const usedKeys = collectKeys(system.menus, false);
    const allKeys = collectKeys(system.menus);
    const unusedKeys = allKeys.filter((key) => !usedKeys.includes(key));
    return {
        granted: countKeys(authMap, usedKeys),
        total: usedKeys.length,
        unusedGranted: countKeys(authMap, unusedKeys),
        unusedTotal: unusedKeys.length,
    };
};

/** 전체 (사용 메뉴 부여 수, 사용 메뉴 수, 미사용 메뉴 부여 수, 미사용 메뉴 수) */
export const countAll = (authMap, systems = []) => {
    return systems.reduce((acc, system) => {
        const count = countSystem(authMap, system);
        return {
            granted: acc.granted + count.granted,
            total: acc.total + count.total,
            unusedGranted: acc.unusedGranted + count.unusedGranted,
            unusedTotal: acc.unusedTotal + count.unusedTotal,
        };
    }, { granted: 0, total: 0, unusedGranted: 0, unusedTotal: 0 });
};

/** 저장 payload (조회 'Y'인 노드만). 등록(REYN)은 수정(UPYN)과 같은 값 */
export const collectGranted = (authMap) => {
    return Object.entries(authMap)
        .filter(([, value]) => value.view === 'Y')
        .map(([key, value]) => {
            const [sytmidno, menuflag] = key.split('_');
            return {
                authidno: Number(sytmidno),
                authflag: Number(menuflag),
                authreyn: yn(value.upyn),
                authupyn: yn(value.upyn),
                authdlyn: yn(value.dlyn),
            };
        })
        .sort((a, b) => (a.authidno - b.authidno) || (a.authflag - b.authflag));
};

/** 두 권한 맵이 같은지 (변경 여부 판단) */
export const isSameAuthMap = (a = {}, b = {}) => {
    const keysA = Object.keys(a).filter((key) => a[key].view === 'Y').sort();
    const keysB = Object.keys(b).filter((key) => b[key].view === 'Y').sort();
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key, idx) => {
        if (key !== keysB[idx]) return false;
        return yn(a[key].upyn) === yn(b[key].upyn) && yn(a[key].dlyn) === yn(b[key].dlyn);
    });
};
